import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Stack, Divider, Button, Switch, Tooltip, FormControl,
  Select, MenuItem, Drawer, IconButton, CircularProgress, Alert, Chip
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../hooks/NotificationContext';
import {
  connectSlack,
  getSlackChannels,
  getSlackChannelConfig,
  getSlackEvents,
  toggleProjectSlack,
  updateSlackChannelMappings,
} from '../../../modules/notification';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ActionButton from '../../../ui/primitives/ActionButton';

const AdminSlackSettings = ({ project, slackIntegration }) => {
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const projectId = project?.id;

  const isConnected = slackIntegration?.slack_integrated ?? false;
  const workspace = slackIntegration?.workspace ?? null;

  const [slackEnabled, setSlackEnabled] = useState(slackIntegration?.workspace?.is_active ?? false);
  const [toggling, setToggling] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [channels, setChannels] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [defaultChannel, setDefaultChannel] = useState('');
  const [perEventPrefs, setPerEventPrefs] = useState({});
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialLoaded = useRef(false);

  const loadConfig = async () => {
    try {
      setConfigLoading(true);

      const [configRes, eventsRes, channelsRes] = await Promise.all([
        getSlackChannelConfig(projectId),
        getSlackEvents(projectId),
        slackEnabled ? getSlackChannels(projectId) : Promise.resolve({ channels: [] }),
      ]);

      const fetchedChannels = channelsRes.channels ?? [];
      const fetchedEvents = eventsRes.events ?? [];
      const rows = configRes.mappings ?? [];

      setChannels(fetchedChannels);
      setAvailableEvents(fetchedEvents);
      setMappings(rows);

      const draft = {};
      fetchedEvents.forEach(e => {
        const hasRow = rows.some(m => m.event_key === e.key);
        draft[e.key] = { enabled: !hasRow, channelId: '' };
      });

      let defCh = '';
      rows.forEach(m => {
        if (m.event_key === 'default') {
          defCh = m.slack_channel_id;
        } else if (m.is_disabled) {
          if (draft[m.event_key] !== undefined) draft[m.event_key] = { enabled: false, channelId: '' };
        } else if (draft[m.event_key] !== undefined) {
          draft[m.event_key] = { enabled: true, channelId: m.slack_channel_id ?? '' };
        }
      });

      setDefaultChannel(defCh);
      setPerEventPrefs(draft);
      initialLoaded.current = true;
    } catch (err) {
      addNotification('danger', err.message);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    if (!isConnected || !projectId || initialLoaded.current) return;
    loadConfig();
  }, [isConnected, projectId]);

  useEffect(() => {
    if (!drawerOpen || !isConnected || !projectId) return;
    loadConfig();
  }, [drawerOpen]);

  const handleConnect = async () => {
    try {
      const { url } = await connectSlack(projectId);
      window.location.assign(url);
    } catch (err) { addNotification('danger', err.message); }
  };

  const handleToggle = async () => {
    const next = !slackEnabled;
    setToggling(true);
    setSlackEnabled(next);
    try {
      await toggleProjectSlack(projectId, next);
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences', projectId] });
    } catch (err) {
      setSlackEnabled(!next);
      addNotification('danger', err.message);
    } finally {
      setToggling(false);
    }
  };

  const handleEventToggle = (key, enabled) =>
    setPerEventPrefs(prev => ({
      ...prev,
      [key]: { enabled, channelId: enabled ? prev[key]?.channelId ?? '' : '' },
    }));

  const handleEventChannel = (key, channelId) =>
    setPerEventPrefs(prev => ({ ...prev, [key]: { ...prev[key], channelId } }));

  const handleDefaultChannelChange = (channelId) => {
    setDefaultChannel(channelId);
    setPerEventPrefs(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (updated[key].channelId === channelId) {
          updated[key] = { ...updated[key], channelId: '' };
        }
      });
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = [
        {
          eventKey: 'default',
          slackChannelId: defaultChannel,
          slackChannelName: channels.find(c => c.id === defaultChannel)?.name ?? '',
        },
        ...Object.entries(perEventPrefs)
          .filter(([, v]) => v.enabled && v.channelId && v.channelId !== defaultChannel)
          .map(([eventKey, v]) => ({
            eventKey,
            slackChannelId: v.channelId,
            slackChannelName: channels.find(c => c.id === v.channelId)?.name ?? '',
          })),
        ...Object.entries(perEventPrefs)
          .filter(([, v]) => !v.enabled)
          .map(([eventKey]) => ({
            eventKey,
            slackChannelId: '',
            slackChannelName: '',
            isDisabled: true,
          })),
      ];

      await updateSlackChannelMappings(projectId, payload);

      setMappings(payload.map(p => ({
        event_key: p.eventKey,
        slack_channel_id: p.slackChannelId,
        slack_channel_name: p.slackChannelName,
        is_disabled: p.isDisabled ?? false,
      })));

      setDrawerOpen(false);
      addNotification('success', 'Slack channel mappings saved.');
    } catch (err) {
      addNotification('danger', err.message);
    } finally {
      setSaving(false);
    }
  };

  const defaultRow = mappings.find(m => m.event_key === 'default');
  const defaultChannelName = defaultRow?.slack_channel_name
    || channels.find(c => c.id === defaultRow?.slack_channel_id)?.name
    || '';

  const customChannelRows = mappings.filter(
    m => m.event_key !== 'default'
      && !m.is_disabled
      && m.slack_channel_id
      && m.slack_channel_id !== defaultRow?.slack_channel_id
  );

  const disabledEventKeys = mappings
    .filter(m => m.is_disabled)
    .map(m => m.event_key);

  const overrideChannels = channels.filter(c => c.id !== defaultChannel);

  return (
    <>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Slack Integration</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Connect a Slack workspace and route notifications to channels.
            </Typography>
          </Box>
          {isConnected && slackEnabled && (
            <Typography
              variant="body2"
              color="success.main"
              sx={{ fontWeight: 'bold', border: '1px solid', borderRadius: 1, px: 2, py: 1 }}
            >
              Connected
            </Typography>
          )}
        </Box>

        {!isConnected ? (
          <Box>
            <Button variant="outlined" size="small" startIcon={<LinkRoundedIcon />} onClick={handleConnect}>
              Connect Slack
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              You'll be redirected to Slack to authorize access.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Switch checked={slackEnabled} onChange={handleToggle} disabled={toggling} size="small" />
              <Typography variant="body2" color={slackEnabled ? 'text.primary' : 'text.secondary'}>
                {slackEnabled ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>

            <Divider />
            {slackEnabled && (
              <>
                <Box>
                  <Typography variant="subtitle2">Connected Workspace:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {workspace?.workspace_name || '—'}
                  </Typography>
                  <Button sx={{ pl: 0 }} size="small" onClick={handleConnect}>
                    Change Workspace
                  </Button>
                </Box>

                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Channel Configuration</Typography>

                    {configLoading ? (
                      <Typography variant="caption" color="text.secondary">Loading…</Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary">
                          Default:{' '}
                          {defaultChannelName
                            ? <strong style={{ color: 'inherit' }}>{defaultChannelName}</strong>
                            : <span style={{ color: '#ed6c02' }}> Not set — If no channel is configured, notifications will not be received. </span>
                          }
                        </Typography>

                        {customChannelRows.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {customChannelRows.map(m => (
                              <Chip
                                key={m.event_key}
                                label={`${availableEvents.find(e => e.key === m.event_key)?.label ?? m.event_key} → ${m.slack_channel_name}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22 }}
                              />
                            ))}
                          </Box>
                        )}

                        {disabledEventKeys.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {disabledEventKeys.map(key => (
                              <Chip
                                key={key}
                                label={`${availableEvents.find(e => e.key === key)?.label ?? key}: Off`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22, opacity: 0.55 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>

                  <Tooltip title="Configure channels">
                    <span>
                      <ActionButton
                        intent="secondary"
                        size="small"
                        label="Configure"
                        tooltip="Configure channels"
                        variant="outlined"
                        icon={<SettingsRoundedIcon fontSize="small" />}
                        onClick={() => setDrawerOpen(true)}
                        disabled={configLoading}
                      />
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}
          </Stack>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !saving && setDrawerOpen(false)}
        PaperProps={{
          className: 'app-right-drawer',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Channel Mapping</Typography>
            <Typography variant="caption" color="text.secondary">Route notifications to Slack channels</Typography>
          </Box>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} disabled={saving}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
          {configLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={32} />
            </Box>
          ) : channels.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>No Slack channels found in this workspace.</Alert>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Default Channel</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  All event notifications go here unless a specific channel is set per event.
                </Typography>
                <FormControl fullWidth size="small">
                  <Select value={defaultChannel} onChange={e => handleDefaultChannelChange(e.target.value)} displayEmpty>
                    <MenuItem value="" sx={{ color: 'text.secondary' }}>None</MenuItem>
                    {channels.map(ch => <MenuItem key={ch.id} value={ch.id} sx={{ color: 'text.secondary' }}>{ch.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>

              {availableEvents.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" fontWeight={600} mb={0.5}>Event Routing</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                    Enable each event type and optionally send it to a different channel than the default.
                  </Typography>

                  <Stack divider={<Divider />}>
                    {availableEvents.map(event => {
                      const pref = perEventPrefs[event.key] ?? { enabled: true, channelId: '' };
                      const effectiveChannelName = pref.channelId
                        ? channels.find(c => c.id === pref.channelId)?.name
                        : defaultChannelName || null;

                      return (
                        <Box key={event.key} sx={{ py: 1.75 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <Typography variant="body2" fontWeight={500}>{event.label}</Typography>
                              {event.description && (
                                <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                                  {event.description}
                                </Typography>
                              )}
                              {pref.enabled && effectiveChannelName && (
                                <Typography variant="caption" color="primary.main" display="block" mt={0.5}>
                                  → {effectiveChannelName}
                                </Typography>
                              )}
                              {pref.enabled && !pref.channelId && !defaultChannel && (
                                <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
                                  → No channel set (set a default channel above)
                                </Typography>
                              )}
                            </Box>
                            <Switch
                              checked={pref.enabled}
                              onChange={e => handleEventToggle(event.key, e.target.checked)}
                              size="small"
                            />
                          </Box>

                          {pref.enabled && (
                            <FormControl fullWidth size="small" sx={{ mt: 1.25 }}>
                              <Select
                                value={pref.channelId}
                                onChange={e => handleEventChannel(event.key, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="" sx={{ color: 'text.secondary' }}>
                                  Use default channel
                                </MenuItem>
                                {overrideChannels.map(ch => (
                                  <MenuItem key={ch.id} value={ch.id} sx={{ color: 'text.secondary' }}>{ch.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, px: 2.5, py: 2 }}>
          <ActionButton
            intent="secondary"
            size="medium"
            label="Cancel"
            onClick={() => setDrawerOpen(false)}
            disabled={saving}
            tooltip="Cancel changes"
          />
          <ActionButton
            intent="primary"
            size="medium"
            label={saving ? 'Saving…' : 'Save Changes'}
            onClick={handleSave}
            disabled={saving || configLoading}
            loading={saving}
            tooltip={saving ? 'Saving changes…' : 'Save channel mappings'}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default AdminSlackSettings;